import { useEffect, useRef, useState, type ReactNode } from 'react'
import { Loader2 } from 'lucide-react'
import { useAuth as useClerkAuth } from '@clerk/react'
import { setSyncHandlers, useStore } from './store'
import { identifyUser, resetUser } from './posthog'
import { resolveOnboardingCompleted } from './workspace-routing'
import type { AgentChat, Channel, ChatMessage, MediaItem, Post, SocialSet } from './types'

// ---------------------------------------------------------------------------
// Token plumbing. The write-through sync handlers below are NOT React
// components, so they can't use hooks. DataBoot installs Clerk's getToken here
// and authFetch attaches the bearer token to every worker request.
// ---------------------------------------------------------------------------
type TokenGetter = () => Promise<string | null>
let getTokenFn: TokenGetter | null = null
export function setTokenGetter(fn: TokenGetter | null) {
  getTokenFn = fn
}

async function authFetch(path: string, opts: RequestInit = {}) {
  const t = getTokenFn ? await getTokenFn() : null
  return fetch((import.meta.env.VITE_API_BASE || '') + path, {
    ...opts,
    headers: {
      'content-type': 'application/json',
      ...(opts.headers || {}),
      ...(t ? { authorization: 'Bearer ' + t } : {}),
    },
  })
}

function logErr(label: string, error: unknown) {
  if (error) console.warn(`[data] ${label}`, error)
}

// ---------------------------------------------------------------------------
// Write-through handlers (store mutation -> worker data API).
// The `ws` arg is ignored: the worker derives the workspace from the token.
// All fire-and-forget; local state already updated optimistically.
// ---------------------------------------------------------------------------
async function dbRemoveChannel(_ws: string, id: string) {
  const r = await authFetch(`/api/data/channels/${id}`, { method: 'DELETE' })
  if (!r.ok) {
    const body = (await r.json().catch(() => ({}))) as { error?: string }
    throw new Error(body.error || `Could not disconnect channel (${r.status})`)
  }
}

async function dbSaveSocialSet(_ws: string, set: SocialSet): Promise<SocialSet> {
  const r = await authFetch(`/api/data/social-sets/${set.id}`, { method: 'PUT', body: JSON.stringify(set) })
  const body = (await r.json().catch(() => ({}))) as { message?: string; socialSet?: SocialSet }
  if (!r.ok || !body.socialSet) throw new Error(body.message || `Could not save set (${r.status})`)
  return body.socialSet
}

async function dbDeleteSocialSet(_ws: string, id: string) {
  const r = await authFetch(`/api/data/social-sets/${id}`, { method: 'DELETE' })
  if (!r.ok) throw new Error(`Could not delete set (${r.status})`)
}

async function dbUpsertPost(_ws: string, p: Post) {
  const r = await authFetch(`/api/data/posts/${p.id}`, { method: 'PUT', body: JSON.stringify(p) })
  const body = (await r.json().catch(() => ({}))) as { error?: string; message?: string; post?: Post | null }
  if (!r.ok) throw new Error(body.message || body.error || `Could not save post (${r.status})`)
  // The worker answers with the post as it now stands, including per-channel
  // delivery state (and, for Post now, the outcome of the publish itself).
  if (body.post) useStore.getState().mergePosts([body.post])
}

async function dbDeletePost(_ws: string, id: string) {
  const r = await authFetch(`/api/data/posts/${id}`, { method: 'DELETE' })
  if (!r.ok) {
    const body = (await r.json().catch(() => ({}))) as { error?: string }
    throw new Error(body.error || `Could not delete post (${r.status})`)
  }
}

async function dbAddMedia(_ws: string, m: MediaItem) {
  try {
    const r = await authFetch('/api/data/media', { method: 'POST', body: JSON.stringify(m) })
    if (!r.ok) logErr('addMedia', `${r.status} ${r.statusText}`)
  } catch (e) {
    logErr('addMedia', e)
  }
}

async function dbRemoveMedia(_ws: string, id: string) {
  try {
    const r = await authFetch(`/api/data/media/${id}`, { method: 'DELETE' })
    if (!r.ok) logErr('removeMedia', `${r.status} ${r.statusText}`)
  } catch (e) {
    logErr('removeMedia', e)
  }
}

async function dbCreateChat(_ws: string, c: AgentChat) {
  try {
    const r = await authFetch('/api/data/chats', { method: 'POST', body: JSON.stringify(c) })
    if (!r.ok) logErr('createChat', `${r.status} ${r.statusText}`)
  } catch (e) {
    logErr('createChat', e)
  }
}

async function dbDeleteChat(_ws: string, id: string) {
  try {
    const r = await authFetch(`/api/data/chats/${id}`, { method: 'DELETE' })
    if (!r.ok) logErr('deleteChat', `${r.status} ${r.statusText}`)
  } catch (e) {
    logErr('deleteChat', e)
  }
}

async function dbPushMessage(_ws: string, chatId: string, m: ChatMessage) {
  // keep the chat title fresh too (first user message renames it locally)
  const title = useStore.getState().chats.find((c) => c.id === chatId)?.title
  try {
    const r = await authFetch(`/api/data/chats/${chatId}/messages`, {
      method: 'POST',
      body: JSON.stringify({ ...m, ...(title ? { title } : {}) }),
    })
    if (!r.ok) logErr('pushMessage', `${r.status} ${r.statusText}`)
  } catch (e) {
    logErr('pushMessage', e)
  }
}

function registerSync() {
  setSyncHandlers({
    removeChannel: (ws, id) => dbRemoveChannel(ws, id),
    saveSocialSet: (ws, set) => dbSaveSocialSet(ws, set),
    deleteSocialSet: (ws, id) => dbDeleteSocialSet(ws, id),
    upsertPost: (ws, p) => dbUpsertPost(ws, p),
    deletePost: (ws, id) => dbDeletePost(ws, id),
    addMedia: (ws, m) => void dbAddMedia(ws, m),
    removeMedia: (ws, id) => void dbRemoveMedia(ws, id),
    createChat: (ws, c) => void dbCreateChat(ws, c),
    deleteChat: (ws, id) => void dbDeleteChat(ws, id),
    pushMessage: (ws, chatId, m) => void dbPushMessage(ws, chatId, m),
  })
}

// ---------------------------------------------------------------------------
// Bootstrap: fetch the whole workspace snapshot from the worker.
// ---------------------------------------------------------------------------
interface Bootstrap {
  serverTime?: number
  workspaceId: string
  onboardingCompleted?: boolean
  channels: Channel[]
  /** Absent from workers that predate social sets. */
  socialSets?: SocialSet[]
  posts: Post[]
  media: MediaItem[]
  chats: AgentChat[]
}

const avatarObjectUrls = new Map<string, string>()

async function loadChannelAvatar(channel: Channel): Promise<Channel> {
  if (!channel.avatarAvailable) return channel
  const cacheKey = channel.id
  const cached = avatarObjectUrls.get(cacheKey)
  if (cached) return { ...channel, avatarUrl: cached }

  try {
    const response = await authFetch(`/api/data/channels/${channel.id}/avatar`, {
      cache: 'force-cache',
    })
    if (!response.ok) return channel
    const objectUrl = URL.createObjectURL(await response.blob())
    avatarObjectUrls.set(cacheKey, objectUrl)
    return { ...channel, avatarUrl: objectUrl }
  } catch {
    return channel
  }
}

async function fetchBootstrap(): Promise<Bootstrap> {
  const r = await authFetch('/api/data/bootstrap', { cache: 'no-store' })
  if (!r.ok) throw new Error(`bootstrap ${r.status} ${r.statusText}`)
  const data = (await r.json()) as Bootstrap
  data.channels = await Promise.all(data.channels.map(loadChannelAvatar))
  lastPostSync = typeof data.serverTime === 'number' ? data.serverTime : Date.now()
  return data
}

// ---------------------------------------------------------------------------
// Live delivery status. While a post is due within the hour or mid-publish,
// the app asks the worker for posts changed since the last sync. Only our own
// Postgres is read; the worker publishes on its own tick regardless.
// ---------------------------------------------------------------------------
export const POST_POLL_MS = 20_000
export const POST_POLL_HORIZON_MS = 60 * 60 * 1000
// Server timestamps drive `since`; this margin absorbs clock skew and a row
// committed in the same millisecond as the previous poll.
const POST_POLL_OVERLAP_MS = 30_000
let lastPostSync = 0

export function shouldPollPosts(posts: Post[], now: number): boolean {
  return posts.some(
    (p) =>
      p.status === 'publishing' ||
      p.status === 'retrying' ||
      ((p.status === 'scheduled' || p.status === 'partial') && p.scheduledAt <= now + POST_POLL_HORIZON_MS),
  )
}

export async function fetchPostChanges(since: number): Promise<{ serverTime: number; posts: Post[] }> {
  const r = await authFetch(`/api/data/posts/changes?since=${Math.max(0, since)}`, { cache: 'no-store' })
  if (!r.ok) throw new Error(`post changes failed (${r.status})`)
  return (await r.json()) as { serverTime: number; posts: Post[] }
}

/** Requeue a post's failed deliveries (all, or one channel) and deliver them now. */
export async function retryPost(postId: string, channelId?: string): Promise<Post | null> {
  const r = await authFetch(`/api/data/posts/${postId}/retry`, {
    method: 'POST',
    body: JSON.stringify(channelId ? { channelId } : {}),
  })
  const body = (await r.json().catch(() => ({}))) as { error?: string; message?: string; post?: Post | null }
  if (!r.ok) {
    if (body.error === 'nothing_to_retry') throw new Error('Nothing left to retry on this post.')
    throw new Error(body.message || body.error || `Retry failed (${r.status})`)
  }
  if (body.post) useStore.getState().mergePosts([body.post])
  return body.post ?? null
}

export function PostChangesPoller() {
  const posts = useStore((s) => s.posts)
  const mergePosts = useStore((s) => s.mergePosts)
  const [clock, setClock] = useState(() => Date.now())
  // Re-evaluate "is anything due soon" once a minute, since that answer
  // changes with time even when no post does.
  useEffect(() => {
    const t = window.setInterval(() => setClock(Date.now()), 60_000)
    return () => window.clearInterval(t)
  }, [])
  const active = shouldPollPosts(posts, clock)
  const inFlight = useRef(false)

  useEffect(() => {
    if (!active) return
    let stopped = false
    let timer: number | undefined
    const schedule = () => {
      timer = window.setTimeout(tick, POST_POLL_MS)
    }
    const tick = async () => {
      if (stopped) return
      if (document.visibilityState === 'hidden' || inFlight.current) {
        schedule()
        return
      }
      inFlight.current = true
      try {
        const since = lastPostSync ? lastPostSync - POST_POLL_OVERLAP_MS : Date.now() - POST_POLL_MS
        const { serverTime, posts: changed } = await fetchPostChanges(since)
        if (stopped) return
        lastPostSync = serverTime
        if (changed.length) mergePosts(changed)
      } catch (e) {
        logErr('postChanges', e)
      } finally {
        inFlight.current = false
      }
      if (!stopped) schedule()
    }
    const onVisible = () => {
      if (document.visibilityState !== 'visible') return
      window.clearTimeout(timer)
      void tick()
    }
    document.addEventListener('visibilitychange', onVisible)
    schedule()
    return () => {
      stopped = true
      window.clearTimeout(timer)
      document.removeEventListener('visibilitychange', onVisible)
    }
  }, [active, mergePosts])

  return null
}

// ---------------------------------------------------------------------------
// Re-hydrate from the worker. Called after a server-side connect (the worker
// writes the channel), so the UI reflects the newly-linked channel.
// ---------------------------------------------------------------------------
export async function reloadWorkspaceData() {
  try {
    const { workspaceId, onboardingCompleted, channels, socialSets, posts, media, chats } = await fetchBootstrap()
    useStore.getState().setActiveWorkspaceId(workspaceId)
    useStore.getState().hydrate({
      onboardingCompleted: resolveOnboardingCompleted(
        onboardingCompleted,
        channels.length + posts.length + media.length + chats.length,
      ),
      channels,
      socialSets,
      posts,
      media,
      chats,
    })
  } catch (e) {
    logErr('reloadWorkspaceData', e)
  }
}

// ---------------------------------------------------------------------------
// Media upload: real file bytes to the worker, which stores them in R2 and
// returns a MediaItem backed by a public URL. Throws when storage is not
// configured (callers fall back to local preview-only media).
// ---------------------------------------------------------------------------
export async function uploadMediaFile(file: File): Promise<MediaItem> {
  const t = getTokenFn ? await getTokenFn() : null
  const qs = new URLSearchParams({ filename: file.name })
  const r = await fetch(
    (import.meta.env.VITE_API_BASE || '') + `/api/data/media/upload?${qs}`,
    {
      method: 'POST',
      headers: {
        'content-type': file.type || 'application/octet-stream',
        ...(t ? { authorization: 'Bearer ' + t } : {}),
      },
      body: file,
    },
  )
  if (!r.ok) {
    const body = await r.json().catch(() => ({}) as { error?: string })
    throw new Error(body.error || `upload failed (${r.status})`)
  }
  return (await r.json()) as MediaItem
}

// ---------------------------------------------------------------------------
// Analytics: published posts with their latest real metric snapshot.
// ---------------------------------------------------------------------------
export interface AnalyticsMetrics {
  impressions: number | null
  likes: number | null
  replies: number | null
  reposts: number | null
  quotes: number | null
  collectedAt: number
}

export interface AnalyticsPost {
  id: string
  content: string
  url: string | null
  publishedAt: number
  channelId: string
  platform: string
  channelName: string
  handle: string
  metrics: AnalyticsMetrics | null
}

export async function fetchAnalytics(days: number): Promise<AnalyticsPost[]> {
  const r = await authFetch(`/api/data/analytics?days=${days}`)
  if (!r.ok) throw new Error(`analytics failed (${r.status})`)
  const data = (await r.json()) as { posts: AnalyticsPost[] }
  return data.posts
}

export async function syncAnalytics(): Promise<void> {
  const r = await authFetch('/api/data/analytics/sync', { method: 'POST' })
  if (!r.ok) {
    const data = await r.json().catch(() => ({})) as { error?: string }
    throw new Error(data.error || `analytics sync failed (${r.status})`)
  }
}

// ---------------------------------------------------------------------------
// Replies: classified cross-network reply feed + triage.
// ---------------------------------------------------------------------------
export type ReplyIntent = 'lead' | 'question' | 'praise' | 'other'

export interface ReplyItem {
  id: string
  provider: string
  intent: ReplyIntent
  isHighSignal: boolean
  authorName: string | null
  authorHandle: string | null
  authorAvatarUrl: string | null
  body: string
  repliedAt: number | null
  readAt: number | null
  doneAt: number | null
  post: {
    id: string
    content: string
    url: string | null
    channelName: string
    channelHandle: string
    platform: string
  }
}

export async function fetchReplies(opts: {
  intent?: ReplyIntent
  openOnly?: boolean
} = {}): Promise<ReplyItem[]> {
  const qs = new URLSearchParams()
  if (opts.intent) qs.set('intent', opts.intent)
  if (opts.openOnly) qs.set('open', 'true')
  const r = await authFetch(`/api/data/replies?${qs}`)
  if (!r.ok) throw new Error(`replies failed (${r.status})`)
  const data = (await r.json()) as { replies: ReplyItem[] }
  return data.replies
}

export async function syncReplies(): Promise<void> {
  const r = await authFetch('/api/data/replies/sync', { method: 'POST' })
  if (!r.ok) {
    const data = await r.json().catch(() => ({})) as { error?: string }
    throw new Error(data.error || `reply sync failed (${r.status})`)
  }
}

export async function patchReply(id: string, patch: { read?: boolean; done?: boolean }): Promise<void> {
  const r = await authFetch(`/api/data/replies/${id}`, { method: 'PATCH', body: JSON.stringify(patch) })
  if (!r.ok) throw new Error(`reply update failed (${r.status})`)
}

// ---------------------------------------------------------------------------
// Onboarding rewards: the "get started, earn bonus credits" checklist.
// ---------------------------------------------------------------------------
export type OnboardingTaskKind = 'activation' | 'social'

export interface OnboardingTask {
  key: string
  kind: OnboardingTaskKind
  credits: number
  title: string
  description: string
  url: string | null
  completed: boolean
  claimed: boolean
}

export interface OnboardingProgress {
  tasks: OnboardingTask[]
  balance: number
  claimedCount: number
  totalCount: number
  earnedCredits: number
  claimableCredits: number
  maxCredits: number
}

export async function fetchOnboardingProgress(): Promise<OnboardingProgress> {
  const r = await authFetch('/api/data/onboarding')
  if (!r.ok) throw new Error(`onboarding failed (${r.status})`)
  return (await r.json()) as OnboardingProgress
}

export async function completeOnboarding(): Promise<void> {
  const r = await authFetch('/api/data/onboarding/complete', { method: 'POST' })
  if (!r.ok) throw new Error(`onboarding completion failed (${r.status})`)
}

export interface ClaimResult {
  result: string
  task: string
  creditsAwarded: number
  balance: number
}

export async function claimOnboardingTask(key: string): Promise<ClaimResult> {
  const r = await authFetch(`/api/data/onboarding/${key}/claim`, { method: 'POST' })
  if (!r.ok) {
    const body = (await r.json().catch(() => ({}))) as { error?: string }
    throw new Error(body.error || `claim failed (${r.status})`)
  }
  return (await r.json()) as ClaimResult
}

// ---------------------------------------------------------------------------
// Workspace settings, members and webhooks.
// ---------------------------------------------------------------------------
export interface WorkspaceInfo {
  workspace: {
    id: string
    name: string
    slug: string | null
    timezone: string
    ownerEmail: string
  }
  members: {
    id: string
    userId: string
    email: string
    name: string | null
    avatarUrl: string | null
    role: string
    invitedAt: number
    acceptedAt: number | null
  }[]
  limits: {
    max_team_members: number
    webhooks_enabled: boolean
    api_enabled: boolean
  } | null
}

export async function getWorkspaceInfo(): Promise<WorkspaceInfo> {
  const r = await authFetch('/api/data/workspace')
  if (!r.ok) throw new Error(`Failed to load workspace (${r.status})`)
  return (await r.json()) as WorkspaceInfo
}

export async function updateWorkspaceInfo(patch: {
  name?: string
  timezone?: string
}): Promise<WorkspaceInfo['workspace']> {
  const r = await authFetch('/api/data/workspace', {
    method: 'PATCH',
    body: JSON.stringify(patch),
  })
  if (!r.ok) throw new Error(`Failed to save workspace (${r.status})`)
  const data = (await r.json()) as { workspace: WorkspaceInfo['workspace'] }
  return data.workspace
}

export type BillingPlanKey = 'standard' | 'team' | 'pro' | 'ultimate'
export type BillingInterval = 'monthly' | 'annual'
export type BillingStatus = 'trialing' | 'active' | 'past_due' | 'canceled'

export interface BillingPlan {
  key: BillingPlanKey
  label: string
  prices: { monthly: number; annual: number }
  limits: {
    accounts: number | null
    posts: number | null
    members: number | null
    monthlyAiCredits: number
    api: boolean
    webhooks: boolean
  }
  configured: boolean
}

export interface BillingSummary {
  catalog: {
    currency: string
    trialDays: number
    plans: BillingPlan[]
  }
  subscription: {
    plan: BillingPlanKey | 'free' | 'starter' | 'legacy_pro' | 'agency' | 'enterprise'
    status: BillingStatus
    interval: BillingInterval | null
    trialEndsAt: number | null
    currentPeriodEnd: number | null
    cancelAtPeriodEnd: boolean
    graceEndsAt: number | null
    hasCustomer: boolean
    hasSubscription: boolean
    hasPaidInvoice: boolean
  }
  usage: {
    accounts: { used: number; limit: number | null }
    posts: { published: number; pending: number; limit: number | null }
    members: { used: number; limit: number | null }
    ai: { balance: number; usedThisPeriod: number; monthlyGrant: number }
  }
  credits: {
    nextGrantAt: number | null
    paymentBlocked: boolean
    history: Array<{ id: string; action: string; delta: number; createdAt: number }>
  }
  access?: { internal: boolean; limitsEnforced: boolean }
  canManage: boolean
}

export async function getBillingSummary(): Promise<BillingSummary> {
  const r = await authFetch('/api/billing/summary')
  if (!r.ok) throw new Error(`Failed to load billing (${r.status})`)
  const body = (await r.json()) as BillingSummary
  body.subscription.hasSubscription ??= body.subscription.hasCustomer && body.subscription.plan !== 'free'
  body.subscription.hasPaidInvoice ??= body.subscription.hasSubscription && body.subscription.plan !== 'free'
  body.credits ||= { nextGrantAt: null, paymentBlocked: false, history: [] }
  return body
}

export async function startBillingCheckout(plan: BillingPlanKey, interval: BillingInterval) {
  const r = await authFetch('/api/billing/checkout', {
    method: 'POST',
    body: JSON.stringify({ plan, interval }),
  })
  const body = (await r.json().catch(() => ({}))) as { url?: string; error?: string }
  if (!r.ok || !body.url) throw new Error(body.error === 'owner_required' ? 'Only the workspace owner can manage billing.' : body.error === 'subscription_exists' ? 'You already have a subscription. Use Manage billing to change plans.' : 'Checkout could not open. Please try again shortly.')
  window.location.assign(body.url)
}

export async function openBillingPortal() {
  const r = await authFetch('/api/billing/portal', { method: 'POST' })
  const body = (await r.json().catch(() => ({}))) as { url?: string; error?: string }
  if (!r.ok || !body.url) throw new Error(body.error === 'owner_required' ? 'Only the workspace owner can manage billing.' : 'Billing could not open. Please try again shortly.')
  window.location.assign(body.url)
}

export interface WebhookEndpoint {
  id: string
  url: string
  events: string[]
  active: boolean
  createdAt: number
}

export async function listWebhooks(): Promise<{
  encryptionReady: boolean
  webhooks: WebhookEndpoint[]
}> {
  const r = await authFetch('/api/data/webhooks')
  if (!r.ok) throw new Error(`Failed to load webhooks (${r.status})`)
  return (await r.json()) as { encryptionReady: boolean; webhooks: WebhookEndpoint[] }
}

export async function createWebhook(input: {
  url: string
  events: string[]
}): Promise<{ webhook: WebhookEndpoint; signingSecret: string }> {
  const r = await authFetch('/api/data/webhooks', {
    method: 'POST',
    body: JSON.stringify(input),
  })
  if (!r.ok) {
    const body = await r.json().catch(() => ({}) as { error?: string })
    throw new Error(body.error || `Failed to create webhook (${r.status})`)
  }
  return (await r.json()) as { webhook: WebhookEndpoint; signingSecret: string }
}

export async function deleteWebhook(id: string): Promise<void> {
  const r = await authFetch(`/api/data/webhooks/${id}`, { method: 'DELETE' })
  if (!r.ok) throw new Error(`Failed to delete webhook (${r.status})`)
}

// ---------------------------------------------------------------------------
// API keys (Settings -> Developers). The n8n / Make / Zapier door. The full
// secret is returned by the server exactly once, on create.
// ---------------------------------------------------------------------------
export interface ApiKey {
  id: string
  name: string
  prefix: string
  scopes: string[]
  lastUsedAt: number | null
  createdAt: number
  revokedAt: number | null
}

export async function listApiKeys(): Promise<ApiKey[]> {
  const r = await authFetch('/api/keys')
  if (!r.ok) throw new Error(`Failed to load API keys (${r.status})`)
  const data = await r.json()
  return data.keys as ApiKey[]
}

export async function createApiKey(name: string): Promise<ApiKey & { key: string }> {
  const r = await authFetch('/api/keys', { method: 'POST', body: JSON.stringify({ name }) })
  if (!r.ok) throw new Error(`Failed to create API key (${r.status})`)
  return (await r.json()) as ApiKey & { key: string }
}

export async function revokeApiKey(id: string): Promise<void> {
  const r = await authFetch(`/api/keys/${id}`, { method: 'DELETE' })
  if (!r.ok) throw new Error(`Failed to revoke API key (${r.status})`)
}

// ---------------------------------------------------------------------------
// DataBoot: gate that wires sync + hydrates the store for the active
// workspace from the worker data API.
// ---------------------------------------------------------------------------
export function DataBoot({ children }: { children: ReactNode }) {
  const { getToken, isSignedIn, userId } = useClerkAuth()
  const [status, setStatus] = useState<'loading' | 'ready' | 'error'>('loading')
  const [attempt, setAttempt] = useState(0)
  const setActiveWorkspaceId = useStore((s) => s.setActiveWorkspaceId)
  const hydrate = useStore((s) => s.hydrate)

  useEffect(() => {
    let cancelled = false
    setStatus('loading')
    setTokenGetter(getToken)
    registerSync()
    ;(async () => {
      if (!isSignedIn) {
        resetUser()
        if (!cancelled) setStatus('ready')
        return
      }
      if (userId) identifyUser(userId)
      try {
        const { workspaceId, onboardingCompleted, channels, socialSets, posts, media, chats } =
          await fetchBootstrap()
        if (cancelled) return
        if (userId) identifyUser(userId, { workspace_id: workspaceId })
        // Set the workspace id so the store's `if (ws)` sync guards fire.
        setActiveWorkspaceId(workspaceId)
        hydrate({
          onboardingCompleted: resolveOnboardingCompleted(
            onboardingCompleted,
            channels.length + posts.length + media.length + chats.length,
          ),
          channels,
          socialSets,
          posts,
          media,
          chats,
        })
        setStatus('ready')
      } catch (e) {
        logErr('bootstrap', e)
        if (!cancelled) setStatus('error')
      }
    })()
    return () => {
      cancelled = true
    }
  }, [attempt, getToken, isSignedIn, userId, hydrate, setActiveWorkspaceId])

  if (status === 'loading')
    return (
      <div className="grid min-h-screen place-items-center">
        <Loader2 className="animate-spin text-brand-400" size={28} />
      </div>
    )
  if (status === 'error')
    return (
      <div className="grid min-h-screen place-items-center p-6 text-center">
        <div>
          <p className="font-medium text-ink-900">Could not load your workspace.</p>
          <p className="mt-1 text-sm text-ink-500">Check your connection and try again.</p>
          <button className="btn-primary mx-auto mt-4" onClick={() => setAttempt((n) => n + 1)}>
            Try again
          </button>
        </div>
      </div>
    )
  return (
    <>
      <PostChangesPoller />
      {children}
    </>
  )
}
